/**
 * sessionParams.ts
 *
 * Pure utility module — no React, no side effects, no imports.
 * Handles session-scoped URL parameter persistence via sessionStorage.
 *
 * Policy: Latest-write wins.
 * URL params captured on the landing are stored for the session.
 * Subsequent page loads with new URL params will overwrite the stored ones
 * to reflect the most recent acquisition.
 *
 * The stored params survive SPA navigations (router.push / Link clicks) but
 * are automatically cleared by the browser when the tab is closed.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

export const SESSION_PARAMS_KEY = 'horizon_session_params';
export const IGNORED_SESSION_PARAMS = new Set(['page', '_gl']);

// ─── Storage I/O ──────────────────────────────────────────────────────────────

/**
 * Persists URL params to sessionStorage using a first-write-wins policy.
 * If params are already stored, this is a safe no-op.
 * Safe to call server-side (returns early if `sessionStorage` is unavailable).
 */
export function storeSessionParams(params: URLSearchParams): void {
  if (typeof sessionStorage === 'undefined') return;

  const filteredParams = new URLSearchParams(params.toString());
  IGNORED_SESSION_PARAMS.forEach((key) => filteredParams.delete(key));

  if (filteredParams.toString() === '') return;

  // Read existing params, if any
  const stored = new URLSearchParams(sessionStorage.getItem(SESSION_PARAMS_KEY) || '');

  let changed = false;

  // Overwrite existing keys if they are present in the new URL
  filteredParams.forEach((value, key) => {
    if (stored.get(key) !== value) {
      stored.set(key, value);
      changed = true;
    }
  });

  if (changed) {
    try {
      sessionStorage.setItem(SESSION_PARAMS_KEY, stored.toString());
    } catch (e) {
      console.warn('[SessionParams] Failed to save to sessionStorage:', e);
    }
  }
}

/**
 * Reads stored URL params from sessionStorage.
 * Returns an empty URLSearchParams if nothing is stored or if called server-side.
 */
export function readSessionParams(): URLSearchParams {
  if (typeof sessionStorage === 'undefined') return new URLSearchParams();

  const stored = sessionStorage.getItem(SESSION_PARAMS_KEY);
  if (!stored) return new URLSearchParams();

  const params = new URLSearchParams(stored);
  let changed = false;

  // Retroactively clean up any ignored params that were saved before this fix
  IGNORED_SESSION_PARAMS.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });

  if (changed) {
    try {
      sessionStorage.setItem(SESSION_PARAMS_KEY, params.toString());
    } catch (e) {
      console.warn('[SessionParams] Failed to save to sessionStorage:', e);
    }
  }

  return params;
}

// ─── Link Helpers ─────────────────────────────────────────────────────────────

/**
 * Determines whether an anchor element represents a forwardable internal link.
 * Excludes: fragment-only links, tel/mailto schemes, and cross-origin links.
 *
 * Extracted from PaidLandingPageProvider so both the PLP provider and the
 * global SessionParamForwarder share a single source of truth.
 */
export function isForwardableInternalLink(anchor: HTMLAnchorElement): boolean {
  if (anchor.hasAttribute('data-ignore-session-params')) return false;

  const rawHref = anchor.getAttribute('href');
  if (!rawHref) return false;
  if (rawHref.startsWith('#')) return false;
  if (rawHref.startsWith('tel:') || rawHref.startsWith('mailto:')) return false;

  let url: URL;
  try {
    url = new URL(anchor.href, window.location.origin);
  } catch {
    return false;
  }

  if (url.origin !== window.location.origin) return false;
  return url.protocol === 'http:' || url.protocol === 'https:';
}

/**
 * Returns a new URL with the stored session params merged into it.
 * Params already present on the destination URL are NOT overwritten —
 * the destination's own params take precedence over stored session params.
 *
 * Extracted from PaidLandingPageProvider so both the PLP provider and the
 * global SessionParamForwarder share a single source of truth.
 */
export function withForwardedParams(href: string, stored: URLSearchParams): URL {
  const url = new URL(href, window.location.origin);
  stored.forEach((value, key) => {
    // Only append if the destination URL doesn't already carry this param.
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });
  return url;
}
